/*
 * First-Sync-Adoption — unberuehrtes Auto-"untitled" gegen Cloud-Projekt tauschen
 *
 * Beim allerersten App-Start legt ProjectManager.initialize() lokal ein
 * leeres "untitled" an, BEVOR der Initial-Sync laeuft. Bringt der Sync dann
 * Cloud-Projekte mit — insbesondere das serverseitig eingespielte
 * Willkommens-Projekt fuer Neuanmeldungen (server/services/seedProject.js),
 * aber genauso die Bestandsprojekte eines Users auf neuem Geraet — soll der
 * User direkt in einem echten Projekt landen statt vor einem leeren Editor.
 * Das nachweislich unberuehrte Auto-"untitled" wird dabei entfernt.
 *
 * Extrahiert aus main.js fuer Testbarkeit (Muster: remoteChangesHandler.js).
 * Abhaengigkeiten werden injiziert, damit der Kern ohne DOM/Bootstrap testbar ist.
 */

const UNTITLED_DEFAULT_TITLE = 'untitled'

/**
 * Prueft, ob das Editor-Model noch den Auslieferungszustand zeigt:
 * genau ein Normal-Paragraph ohne Text, Note und Info.
 * @param {Object|null} editorModel
 * @returns {boolean}
 */
function isPristineModel(editorModel) {
    const paragraphs = editorModel?.paragraphs || []
    if (paragraphs.length !== 1) return false
    const para = paragraphs[0]
    if (!para.isNormal || !para.isNormal()) return false
    return (para.text || '') === ''
        && (para.note || '') === ''
        && (para.info || '') === ''
}

/**
 * Ersetzt nach dem Initial-Sync das unberuehrte lokale Auto-"untitled" durch
 * das neueste Cloud-Projekt: Cloud-Projekt oeffnen, untitled-Tab schliessen,
 * untitled permanent loeschen (kein Papierkorb — es war nie Nutzer-Content).
 *
 * Greift bewusst NUR, wenn alle Bedingungen erfuellt sind:
 *  - der Sync hat mindestens ein Cloud-Projekt gebracht,
 *  - das aktuelle Projekt ist lokal (nicht cloudEnabled) und heisst "untitled",
 *  - das Live-Editor-Model ist nachweislich leer (nichts getippt).
 *
 * @param {Object} deps
 * @param {Object} deps.projectManager - ProjectManager-Instanz
 * @param {Object|null} deps.tabManager - TabManager (optional, Bootstrap-Race)
 * @param {Object|null} deps.editorModel - EditorModel des aktiven Editors
 * @param {string|null} deps.currentProjectId - ID des aktuell geladenen Projekts
 * @param {Function} deps.switchToProject - async (projectId) => void
 * @returns {Promise<boolean>} true wenn getauscht wurde
 */
export async function replaceUntouchedUntitledWithCloudProject({
    projectManager,
    tabManager,
    editorModel,
    currentProjectId,
    switchToProject
}) {
    try {
        if (!projectManager || !currentProjectId) return false

        const projects = await projectManager.getAllProjects()
        const cloudProjects = projects.filter(p => p.cloudEnabled)
        if (cloudProjects.length === 0) return false

        const current = projects.find(p => p.id === currentProjectId)
        if (!current || current.cloudEnabled) return false
        if (current.title !== UNTITLED_DEFAULT_TITLE) return false
        if (!isPristineModel(editorModel)) return false

        // Neuestes Cloud-Projekt oeffnen — bei einem frischen Seed-User ist
        // das genau das Willkommens-Projekt.
        const target = [...cloudProjects]
            .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))[0]

        await switchToProject(target.id)
        if (tabManager) tabManager.closeTab(current.id)
        await projectManager.permanentDeleteProject(current.id)
        return true
    } catch (err) {
        console.warn('First-sync adoption failed:', err && err.message)
        return false
    }
}
